// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import RastreioDeRota from './components/Analytics/RastreioDeRota';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import Home from './pages/Home/Home';
import PropertyList from './pages/PropertyList/PropertyList';
import PropertyDetail from './pages/PropertyDetail/PropertyDetail';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import Dashboard from './pages/Dashboard/Dashboard';
import ProtectedRoute from './components/common/ProtectedRoute';
import AddProperty from './pages/AddProperty/AddProperty';
import EditProperty from './pages/EditProperty/EditProperty';
import SmartSearchPage from './pages/SmartSearch/SmartSearchPage';
import SellerProfile from './pages/SellerProfile';
import MyAccount from './pages/MyAccount/MyAccount';
import BoostProperty from './pages/BoostProperty/BoostProperty';
import Checkout from './pages/Checkout/Checkout';
import PaymentSuccess from './pages/PaymentSuccess/PaymentSuccess';
import AdminFeatured from './pages/Admin/AdminFeatured';
import AdminApproval from './pages/Admin/AdminApproval';
import AdminProperties from './pages/Admin/AdminProperties';
import AdminUsers from './pages/Admin/AdminUsers';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';

// Institutional Pages
import AboutUs from './pages/Institutional/AboutUs';
import HowItWorks from './pages/Institutional/HowItWorks';
import ForOwners from './pages/Institutional/ForOwners';
import ForProfessionals from './pages/Institutional/ForProfessionals';
import Contact from './pages/Institutional/Contact';
import TermsOfService from './pages/Institutional/TermsOfService';
import PrivacyPolicy from './pages/Institutional/PrivacyPolicy';

// Chapecó Content Pages
import ChapecoPage from './pages/Chapeco/ChapecoPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF7F27',
      light: '#FFA05A',
      dark: '#E66A0F',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1A1A1A',
      light: '#333333',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    // ---------------------------------------------------------------------
    // REGUA DE LAYOUT UMARKT
    //
    // Escala de espacamento, base 8 (o valor de theme.spacing padrao do MUI).
    // Usar sempre estes degraus, nunca numeros soltos:
    //   0.5=4  1=8  1.5=12  2=16  3=24  4=32  6=48  9=72  12=96
    // Base 8 porque toda densidade de tela comum (1x, 1.5x, 2x, 3x) divide 8
    // em pixel inteiro - com base 10 o meio-degrau vira fracao e o navegador
    // arredonda diferente em cada zoom.
    //
    // TRILHO UNICO: todo bloco de pagina para na mesma linha vertical.
    // O codigo tem 46 <Container> misturando xl / lg / md, o que fazia cada
    // secao comecar num lugar diferente. Em vez de editar 46 arquivos,
    // normalizamos as variantes aqui: xl e lg passam a valer o mesmo trilho
    // de 1280px. Assim o alinhamento e uma decisao unica, no tema.
    //
    // md continua menor de proposito: e a LARGURA DE LEITURA (768px, ~70
    // caracteres por linha). Mas largura de leitura NAO e centralizacao -
    // ela encosta na margem esquerda do trilho (ver <Leitura> em
    // components/Layout/Secao.js). Bloco de texto centralizado dentro de um
    // trilho largo foi exatamente o erro que aparecia no FAQ.
    //
    // REGRA: nunca misturar md com xl/lg na MESMA pagina. Container md e
    // centralizado; ao lado de um bloco xl ele cria uma segunda margem
    // esquerda. Medido em 1200px: xl comeca em 32, md comeca em 248 - 216px
    // de desalinhamento visivel. Bloco de texto dentro de pagina larga usa
    // <Secao> + <Leitura>, nunca Container md.
    //
    // UNICA EXCECAO DECLARADA: as telas de autenticacao (Login, Esqueci a
    // senha, Registro) usam cartao centralizado. Ali a pagina tem UM objeto
    // so e nenhum bloco de conteudo concorrendo - centralizar e o padrao que
    // a pessoa espera. E excecao nomeada, nao descuido: qualquer outra tela
    // que aparecer fora do trilho e bug.
    //
    // As breakpoints do MUI ficam nos valores de fabrica (600/900/1200/1536)
    // de proposito: mexer nelas mudaria o sentido de todo prop responsivo
    // ja escrito no projeto.
    // ---------------------------------------------------------------------
    MuiContainer: {
      styleOverrides: {
        // Goteira em tres degraus (2 / 3 / 4 da escala). Os tres estao
        // declarados de proposito: o MUI ja traz um @media 600px proprio de
        // 24px que vence um paddingLeft sem media, entao omitir o degrau do
        // meio nao o remove - so o esconde de quem le o tema.
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          '@media (min-width:600px)': { paddingLeft: 24, paddingRight: 24 },
          '@media (min-width:900px)': { paddingLeft: 32, paddingRight: 32 },
        },
        maxWidthXl: { '@media (min-width:0px)': { maxWidth: 1280 } },
        maxWidthLg: { '@media (min-width:0px)': { maxWidth: 1280 } },
        maxWidthMd: { '@media (min-width:0px)': { maxWidth: 768 } },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #FF7F27 0%, #FF9A52 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E66A0F 0%, #FF7F27 100%)',
            boxShadow: '0px 4px 12px rgba(255, 127, 39, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          {/* page_view a cada troca de rota — sem isto o GA4 registra
              apenas a home, porque a SPA nunca recarrega a pagina */}
          <RastreioDeRota />
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
            <Navbar />
            <Box sx={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/busca" element={<SmartSearchPage />} />
                <Route path="/imoveis" element={<PropertyList />} />
                <Route path="/imoveis/:id" element={<PropertyDetail />} />

                <Route path="/vendedor/:username" element={<SellerProfile />} />
                <Route path="/vendedores/:username" element={<SellerProfile />} />

                <Route path="/login" element={<Login />} />
                <Route path="/registro" element={<Register />} />
                <Route path="/esqueci-senha" element={<ForgotPassword />} />
                <Route path="/redefinir-senha/:uid/:token" element={<ResetPassword />} />

                <Route
                  path="/dashboard"
                  element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
                />
                <Route
                  path="/dashboard/novo-imovel"
                  element={<ProtectedRoute><AddProperty /></ProtectedRoute>}
                />
                <Route
                  path="/dashboard/editar-imovel/:id"
                  element={<ProtectedRoute><EditProperty /></ProtectedRoute>}
                />
                <Route
                  path="/minha-conta"
                  element={<ProtectedRoute><MyAccount /></ProtectedRoute>}
                />

                {/* Rotas de Impulsionamento e Pagamento */}
                <Route
                  path="/impulsionar/:propertyId"
                  element={<ProtectedRoute><BoostProperty /></ProtectedRoute>}
                />
                <Route
                  path="/checkout/:paymentId"
                  element={<ProtectedRoute><Checkout /></ProtectedRoute>}
                />
                <Route
                  path="/payment-success"
                  element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>}
                />

                {/* Admin Pages */}
                <Route
                  path="/painel/destaques"
                  element={<ProtectedRoute><AdminFeatured /></ProtectedRoute>}
                />
                <Route
                  path="/painel/aprovacoes"
                  element={<ProtectedRoute><AdminApproval /></ProtectedRoute>}
                />
                <Route
                  path="/painel/imoveis"
                  element={<ProtectedRoute><AdminProperties /></ProtectedRoute>}
                />
                <Route
                  path="/painel/usuarios"
                  element={<ProtectedRoute><AdminUsers /></ProtectedRoute>}
                />

                {/* Institutional Pages */}
                <Route path="/sobre-nos" element={<AboutUs />} />
                <Route path="/como-funciona" element={<HowItWorks />} />
                <Route path="/para-proprietarios" element={<ForOwners />} />
                <Route path="/para-profissionais" element={<ForProfessionals />} />
                <Route path="/contato" element={<Contact />} />
                <Route path="/termos-de-uso" element={<TermsOfService />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />

                {/* Chapecó Content Pages */}
                <Route path="/chapeco/:slug" element={<ChapecoPage />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
